import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Field, Image, Input, Spinner, Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import type { Bridge, BrowsedService } from '@casa/bridge';
import { isServerPage, readServerInfo } from '../data/casa_server.ts';
import { DashboardError, announceDashboard, createDashboard, keepNewDashboard, loadFromServer, suggestedName } from '../data/dashboard.ts';
import { freeDeviceName } from '../data/device_store.ts';
import { listStore, readStoreSettings, writeStoreSettings, type StoreEntry, type StoreSettings, type WritableStore } from '../data/stores.ts';
import { requestEditMode } from '../editing/editor.tsx';
import { ChromeError } from '../ui/chrome_page.tsx';
import { DEMO_DASHBOARDS, demoName, type DemoVersion } from './demo_dashboard.ts';
import { goToSettings } from './settings_navigation.ts';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';
import { pairOrSignIn, useFoundServers } from './store_part.tsx';

// The first start, while this device has no dashboard (docs/specs/settings.md §4.6): the app's name,
// Try the demo and Make my own dashboard; a Tilez Hub found on the network first (FS-2), used at
// once (FS-3); the other ways (a file, a server by its address, a web address) only when asked for.
// In a browser, which a Tilez Hub served, that server's dashboards to choose from (FS-5).

const TAGLINE = 'Your home on one screen';
const OWN_NAME = 'My home';

function messageOf(error: unknown) : string {
    const issues = error instanceof DashboardError ? error.issues.map((issue) => `\n- ${issue}`).join('') : '';

    return `${error instanceof Error ? error.message : String(error)}${issues}`;
}

/** A found server whose way in is being asked for: a code, or a name and password where it has users. */
interface Pairing {
    server: BrowsedService;
    signsIn: boolean;
}

/** The fields under a found server for its code, or its user's name and password. */
function PairingFields({ pairing, isBusy, onPair }: { pairing: Pairing; isBusy: boolean; onPair: (way: { code: string } | { user: string; password: string }) => void }) : ReactNode {
    const [code, setCode] = useState('');
    const [user, setUser] = useState('');
    const [password, setPassword] = useState('');

    if (pairing.signsIn) {
        return (
            <Stack gap="3" p="4">
                <Field.Root>
                    <Field.Label>Name</Field.Label>
                    <Input value={user} autoComplete="username" aria-label="Name" onChange={(event) => setUser(event.target.value)} />
                </Field.Root>
                <Field.Root>
                    <Field.Label>Password</Field.Label>
                    <Input type="password" value={password} autoComplete="current-password" aria-label="Password" onChange={(event) => setPassword(event.target.value)} />
                </Field.Root>
                <Button colorPalette="blue" alignSelf="flex-start" disabled={user.trim() === '' || password === ''} loading={isBusy} onClick={() => onPair({ user, password })}>
                    Sign in
                </Button>
            </Stack>
        );
    }

    return (
        <Stack gap="3" p="4">
            <Field.Root>
                <Field.Label>Pairing code</Field.Label>
                <Input placeholder="ABCD-EFGH" value={code} aria-label="Pairing code" autoFocus onChange={(event) => setCode(event.target.value)} />
                <Field.HelperText>On the server's admin page in a browser (its address followed by /admin).</Field.HelperText>
            </Field.Root>
            <Button colorPalette="blue" alignSelf="flex-start" disabled={code.trim() === ''} loading={isBusy} onClick={() => onPair({ code: code.trim() })}>
                Pair
            </Button>
        </Stack>
    );
}

export function FirstStartPage({ shell }: { shell: Bridge }) : ReactNode {
    const isOnServer = isServerPage();
    const [settings, setSettings] = useState<StoreSettings>();
    /** The dashboards of the Tilez Hub this device uses: in a browser at once, in an app once paired. */
    const [serverDashboards, setServerDashboards] = useState<StoreEntry[]>();
    const [pairing, setPairing] = useState<Pairing>();
    /** What is being done: 'demo', 'own', 'import', a server's name or a dashboard's. */
    const [busy, setBusy] = useState<string>();
    const [error, setError] = useState<string>();
    const [showsOtherWays, setShowsOtherWays] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const looked = useFoundServers(shell);
    const found = !isOnServer && serverDashboards === undefined && looked.status === 'done' ? looked.servers : [];
    // The phone's version where the app shows the phone's menu (dashboard_app.tsx), else the tablet's.
    const fitting: DemoVersion = useBreakpointValue({ base: true, md: false }, { ssr: false }) ? 'phone' : 'tablet';

    useEffect(() => {
        let isCurrent = true;

        void (async () => {
            const read = await readStoreSettings(shell);

            if (!isCurrent) {
                return;
            }

            setSettings(read);

            if (isOnServer) {
                const listed = await listStore(shell, 'casa').catch(() => []);

                if (isCurrent) {
                    setServerDashboards(listed);
                }
            }
        })();

        return () => {
            isCurrent = false;
        };
    }, [shell, isOnServer]);

    // New dashboards go to the current store: this device on a fresh install, the server in a browser.
    const store: WritableStore = settings?.current === 'casa' || settings?.current === 'cloud' ? settings.current : 'device';

    /** Runs one of the page's actions, showing what it is busy with and what went wrong. */
    const run = async (what: string, action: () => Promise<void>) : Promise<void> => {
        setBusy(what);
        setError(undefined);

        try {
            await action();
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setBusy(undefined);
        }
    };

    const takenNames = async () : Promise<string[]> => {
        const entries = await listStore(shell, store).catch(() => []);

        return entries.map((entry) => entry.name);
    };

    const tryDemo = () : Promise<void> =>
        run('demo', async () => {
            announceDashboard(await keepNewDashboard(shell, DEMO_DASHBOARDS[fitting], store, demoName(await takenNames())));
        });

    /** "My home", laid out for the window it starts in, opened in edit mode (FS-4, QF-2). */
    const makeOwn = () : Promise<void> =>
        run('own', async () => {
            const grid = fitting === 'phone' ? { columns: 4 } : { columns: 64, rows: 64 };
            const name = store === 'device' ? await freeDeviceName(shell, OWN_NAME) : OWN_NAME;
            const dashboard = await createDashboard(shell, grid, store, name);

            requestEditMode();
            announceDashboard(dashboard);
        });

    const importFile = (file: File | undefined) : Promise<void> =>
        run('import', async () => {
            if (!file) {
                return;
            }

            const suggested = suggestedName(file.name, 'Imported');
            const name = store === 'device' ? await freeDeviceName(shell, suggested) : suggested;

            announceDashboard(await keepNewDashboard(shell, await file.text(), store, name));
        });

    const open = (name: string) : Promise<void> =>
        run(name, async () => {
            announceDashboard(await loadFromServer(shell, name));
        });

    /** Paired: the server is the current store, and its one dashboard opens; several are listed (FS-3). */
    const usePaired = async () : Promise<void> => {
        const next: StoreSettings = { ...(settings ?? { current: 'device' }), current: 'casa' };

        await writeStoreSettings(shell, next);
        setSettings(next);
        setPairing(undefined);

        const listed = await listStore(shell, 'casa');
        const only = listed.length === 1 ? listed[0] : undefined;

        if (only) {
            announceDashboard(await loadFromServer(shell, only.name));
            return;
        }

        setServerDashboards(listed);
    };

    const pair = (server: BrowsedService, way: { code: string } | { user: string; password: string }) : Promise<void> =>
        run(server.name, async () => {
            await pairOrSignIn(shell, server.txt.url ?? '', way);
            await usePaired();
        });

    /** A found server: paired at once where it needs no code; otherwise its way in is asked for under it. */
    const chooseServer = (server: BrowsedService) : Promise<void> =>
        run(server.name, async () => {
            const url = server.txt.url ?? '';

            if (server.txt.pairing === 'open') {
                await pairOrSignIn(shell, url, { code: '' });
                await usePaired();
                return;
            }

            const info = await readServerInfo(shell, url);

            setPairing({ server, signsIn: info?.signIn.includes('password') === true });
        });

    if (!settings) {
        return null;
    }

    const serverList = serverDashboards?.length ? (
        <SettingsGroup title={isOnServer ? 'On this Tilez Hub' : 'On the Tilez Hub'}>
            {serverDashboards.map((entry) => (
                <SettingsRow
                    key={entry.name}
                    icon="table-cells-large"
                    title={entry.name}
                    onClick={() => void open(entry.name)}
                    end={busy === entry.name ? <Spinner size="sm" /> : undefined}
                    data-testid={`dashboard-${entry.name}`}
                />
            ))}
        </SettingsGroup>
    ) : null;

    return (
        <Box className="chrome" height="100%" overflowY="auto" overscrollBehavior="none" bg="bg" color="fg">
            <Stack gap="8" px={{ base: '4', md: '8' }} py={{ base: '10', md: '16' }} maxW="md" mx="auto">
                <Stack gap="3" align="center" textAlign="center">
                    <Image src="./app-icon.svg" alt="" boxSize="20" borderRadius="2xl" />
                    <Text fontSize="3xl" fontWeight="bold">
                        Tilez
                    </Text>
                    <Text color="fg.muted">{TAGLINE}</Text>
                </Stack>

                {found.length > 0 ? (
                    <SettingsGroup title="Found on your network">
                        {found.map((server) => (
                            <Box key={server.name}>
                                <SettingsRow
                                    icon="server"
                                    title={server.name}
                                    detail="Use its dashboards"
                                    onClick={() => void chooseServer(server)}
                                    end={busy === server.name ? <Spinner size="sm" /> : undefined}
                                />
                                {pairing?.server.name === server.name ? (
                                    <PairingFields pairing={pairing} isBusy={busy === server.name} onPair={(way) => void pair(server, way)} />
                                ) : null}
                            </Box>
                        ))}
                    </SettingsGroup>
                ) : null}
                {serverList}
                {serverDashboards?.length === 0 && !isOnServer ? <Text color="fg.muted" textAlign="center">This server has no dashboards yet.</Text> : null}

                <Stack gap="3">
                    <Button size="xl" colorPalette="blue" loading={busy === 'demo'} onClick={() => void tryDemo()}>
                        Try the demo
                    </Button>
                    <Button size="xl" variant="outline" loading={busy === 'own'} onClick={() => void makeOwn()}>
                        Make my own dashboard
                    </Button>
                </Stack>

                {error ? <ChromeError text={error} /> : null}

                <Stack gap="3">
                    <Button variant="ghost" size="sm" alignSelf="center" color="fg.muted" aria-expanded={showsOtherWays} onClick={() => setShowsOtherWays(!showsOtherWays)}>
                        Other ways to start {showsOtherWays ? '‹' : '›'}
                    </Button>
                    {showsOtherWays ? (
                        <SettingsGroup>
                            <SettingsRow
                                icon="file-import"
                                title="Import a file"
                                onClick={() => fileInput.current?.click()}
                                end={busy === 'import' ? <Spinner size="sm" /> : undefined}
                            />
                            {isOnServer ? null : <SettingsRow icon="cloud" title="Tilez Cloud" detail="With a pairing code" onClick={() => goToSettings('store')} />}
                            {isOnServer ? null : <SettingsRow icon="server" title="A Tilez Hub, by its address" onClick={() => goToSettings('store')} />}
                            {isOnServer ? null : <SettingsRow icon="globe" title="A web address" onClick={() => goToSettings('store')} />}
                        </SettingsGroup>
                    ) : null}
                </Stack>
                <input
                    ref={fileInput}
                    type="file"
                    accept=".json,application/json"
                    hidden
                    data-testid="import-file"
                    onChange={(event) => {
                        void importFile(event.target.files?.[0]);
                        event.target.value = '';
                    }}
                />
            </Stack>
        </Box>
    );
}
